
import { SEARCH_MILES_OPTIONS, AUTO_SEARCH_TYPE_OPTIONS, PROPERTY_SEARCH_TYPE_OPTIONS, PROVIDER_CLAIM_VENDOR_TYPE_OPTIONS } from '../constants/providerSearchConstants.js';
import { FORM_TEXT } from '../constants/textConstants.js';
import type { InitialValues } from './validationConfig.js';
import { ALPHANUMERIC_REGEX, US_ZIP_CODE_REGEX, ZIP_INPUT_REGEX, VALIDATION_MESSAGES } from '../constants/validationConstants.js';
import { getStateOptions, getPropertyVendorTypeOptions, buildPropertyServiceCodeOptions } from '../config/updateProviderConfig';

// Form field configuration types

// Interface for requiredEitherOr validation rule
export interface RequiredEitherOrRule {
  fields: string[];  // Array of field names to watch for either/or validation
  message: string;   // Custom validation message
}

// Interface for conditionalGroup validation rule
export interface ConditionalGroupValidationRule {
  fields: [string[], string];
  message: string
}

// Base validation rules (common to both required types)
interface BaseValidationRules {
  pattern?: RegExp | { value: RegExp; message: string };
  maxLength?: number | { value: number; message: string };
  minLength?: number | { value: number; message: string };
}

// Union type: Either required OR requiredEitherOr OR conditionalGroup
export type ValidationRules = BaseValidationRules & (
  | { required: boolean | { value: boolean; message: string }; requiredEitherOr?: never; conditionalGroup?: never }
  | { required?: never; requiredEitherOr: RequiredEitherOrRule; conditionalGroup?: never }
  | { required?: never; requiredEitherOr?: never; conditionalGroup: ConditionalGroupValidationRule }
  | { required?: never; requiredEitherOr?: never; conditionalGroup?: never } // Neither (optional field)
);

export interface FieldConfig {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'number' | 'email' | 'radio' | 'multiselect' | 'multiselecttag' | 'textarea' | 'datePicker' | 'textselect';
  label: string;
  placeholder?: string;
  value?: string | string[];
  disabled?: boolean;
  customClassName?: string;
  colSize: 'sm' | 'md' | 'lg' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | '11' | '12';
  // Responsive column sizes
  colSizeSm?: 'sm' | 'md' | 'lg' | '1' | '2' | '3' | '4' | '6' | '7' | '8' | '12';
  colSizeMd?: 'sm' | 'md' | 'lg' | '1' | '2' | '3' | '4' | '6' | '7' | '8' | '12';
  colSizeLg?: 'sm' | 'md' | 'lg' | '1' | '2' | '3' | '4' | '6' | '7' | '8' | '12';
  options?: Array<{ id: string; value: string; group?: string }>;
  validation?: ValidationRules;
  limitations?: {
    pattern?: RegExp;
    maxLength?: number;
  };
  isMulti?: boolean;  // Add this property
  required?: boolean;
  showIf?: { field: string; value: string };
  maxSelection?: number;
  helpText?: string;
  filter?: boolean;
  groupBy?: string;
  disabledIf?: DisabledIfCondition;
}

// Condition for disabling a field based on other field values
export interface DisabledIfCondition {
  // Simple form: disable if another field equals a value
  field?: string;
  value?: string;

  // Or: disable if another field is non-empty
  notEmpty?: boolean;

  // Compound form: disable if ANY of these conditions are true
  anyOf?: DisabledIfCondition[];
}
// Generic Form configuration interface
export interface FormConfig {
  title: string;
  fields: FieldConfig[];
  rows?: string[][];
  helpText?: string;
  initialValues: InitialValues;
}

// External config: local and name, each mapping to MainFormConfig
export interface ExternalConfig {
  [type: string]: FormConfig; // type can be 'local' or 'name'
}

// Combined config type for admin and external
export interface CombinedFormConfigs {
  admin: FormConfig;
  external: ExternalConfig;
}



// Form Configurations for Different Search Types and User Roles
export const AUTO_SEARCH_CONFIGS: CombinedFormConfigs = {
  admin: {
    title: "Provider Search",
    helpText: "Enter Vendor Name or ZIP code to search. Other fields are optional and can help refine results.",
    fields: [
      {
        name: 'vendorName',
        type: 'text',
        label: 'Vendor Name',
        colSize: '12',
        colSizeSm: '6',
        colSizeMd: '6',
        colSizeLg: '6',
        validation: {
          requiredEitherOr: {
            fields: ['vendorName', 'zip'],
            message: VALIDATION_MESSAGES.EITHER_VENDOR_NAME_OR_ZIP_CODE
          },
          minLength: { value: 3, message: VALIDATION_MESSAGES.VENDOR_NAME_MIN_LENGTH }
        }
      },
      {
        name: 'vendorId',
        type: 'text',
        label: 'Vendor ID',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
        limitations: {
          pattern: ALPHANUMERIC_REGEX,
        }
      },
      {
        name: 'address',
        type: 'text',
        label: 'Address',
        customClassName: 'address-field',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3'
      },
      {
        name: 'city',
        type: 'text',
        label: 'City',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3'
      },
      {
        name: 'state',
        type: 'select',
        label: 'State',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
        options: []
      },
      {
        name: 'zip',
        type: 'text',
        label: 'ZIP',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
        validation: {
          requiredEitherOr: {
            fields: ['vendorName', 'zip'],
            message: VALIDATION_MESSAGES.EITHER_VENDOR_NAME_OR_ZIP_CODE
          },
          pattern: { value: US_ZIP_CODE_REGEX, message: VALIDATION_MESSAGES.ZIP_CODE_INVALID },
        },
        limitations: {
          pattern: ZIP_INPUT_REGEX,
          maxLength: 5
        }
      },
      {
        name: 'networkSelection',
        type: 'select',
        label: 'Network Selection',
        disabled: true,
        customClassName: 'network-select-field',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
        options: [],
      }
    ],
    rows: [
      ['vendorName', 'vendorId'],
      ['address', 'city', 'state', 'zip'],
      ['networkSelection']
    ],
    initialValues: {
      vendorName: '',
      vendorId: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      networkSelection: FORM_TEXT.NETWORK_SELECTION
    }
  },
  external: {
    local: {
      title: "Auto Repair Shops Search",
      helpText: 'Enter  City and State or ZIP to search. All other fields are optional and can help refine results. ',
      fields: [
        {
          name: "searchType",
          type: "select",
          label: "Auto Search Type",
          options: AUTO_SEARCH_TYPE_OPTIONS,
          colSize: '12',
          colSizeSm: '12',
          colSizeMd: '3',
          colSizeLg: '3',
        },
        {
          name: 'address',
          type: 'text',
          label: 'Address',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
        },
        {
          name: 'city',
          type: 'text',
          label: 'City',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          validation: {
            conditionalGroup: {
              fields: [['city', 'state'], 'zip'],
              message: VALIDATION_MESSAGES.CITY_STATE_OR_ZIP_REQUIRED
            }
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          options: [],
          validation: {
            conditionalGroup: {
              fields: [['city', 'state'], 'zip'],
              message: VALIDATION_MESSAGES.CITY_STATE_OR_ZIP_REQUIRED
            }
          }
        },
        {
          name: 'zip',
          type: 'text',
          label: 'ZIP',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          validation: {
            conditionalGroup: {
              fields: [['city', 'state'], 'zip'],
              message: VALIDATION_MESSAGES.CITY_STATE_OR_ZIP_REQUIRED
            },
            pattern: { value: US_ZIP_CODE_REGEX, message: VALIDATION_MESSAGES.ZIP_CODE_INVALID },
          },
          limitations: {
            maxLength: 5,
            pattern: ZIP_INPUT_REGEX
          }
        },
        {
          name: 'networkSelection',
          type: 'select',
          label: 'Network Selection',
          colSize: '12',
          colSizeSm: '12',
          colSizeMd: '4',
          colSizeLg: '3',
          options: [],
          disabled: true
        },
        {
          name: 'searchDistance',
          type: 'select',
          label: 'Search Distance (Miles)',
          colSize: '12',
          colSizeSm: '12',
          colSizeMd: '4',
          colSizeLg: '3',
          options: SEARCH_MILES_OPTIONS,
        }
      ],
      rows: [
        ["address", "city", "state", "zip"],
        ["networkSelection", "searchDistance"]
      ],
      initialValues: {
        searchType: 'local',
        address: '',
        city: '',
        state: '',
        zip: '',
        networkSelection: FORM_TEXT.NETWORK_SELECTION,
        searchDistance: '25'
      }
    },
    name: {
      title: "Auto Repair Shops Search",
      helpText: 'Enter Vendor Name and State to search. City is optional and can help refine results.',
      fields: [
        {
          name: "searchType",
          type: "select",
          label: "Auto Search Type",
          options: AUTO_SEARCH_TYPE_OPTIONS,
          colSize: '3',
          colSizeSm: '12',
          colSizeMd: '3',
          colSizeLg: '3',
        },
        {
          name: 'vendorName',
          type: 'text',
          label: 'Vendor Name',
          colSize: '12',
          colSizeSm: '7',
          colSizeMd: '7',
          colSizeLg: '7',
          validation: {
            minLength: { value: 3, message: VALIDATION_MESSAGES.VENDOR_NAME_MIN_LENGTH },
            required: { value: true, message: VALIDATION_MESSAGES.VENDOR_NAME_REQUIRED }
          }
        },
        {
          name: 'city',
          type: 'text',
          label: 'City',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',

        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          options: [],
        },
        {
          name: 'networkSelection',
          type: 'select',
          label: 'Network Selection',
          colSize: '12',
          colSizeSm: '12',
          colSizeMd: '4',
          colSizeLg: '3',
          options: [],
          disabled: true
        }
      ],
      rows: [
        ["vendorName"],
        ["city", "state", "networkSelection"]
      ],
      initialValues: {
        searchType: 'name',
        vendorName: '',
        city: '',
        state: '',
        networkSelection: FORM_TEXT.NETWORK_SELECTION,
      }
    }
  }
};

// Property Search Configuration
export const PROPERTY_SEARCH_CONFIGS: CombinedFormConfigs = {
  admin: {
    title: "Provider Search",
    fields: [
      {
        name: 'vendorName',
        type: 'text',
        label: 'Vendor Name',
        colSize: '12',
        colSizeSm: '6',
        colSizeMd: '6',
        colSizeLg: '6',
        validation: {
          minLength: { value: 3, message: VALIDATION_MESSAGES.VENDOR_NAME_MIN_LENGTH },
          requiredEitherOr: {
            fields: ['vendorName', 'zip'],
            message: VALIDATION_MESSAGES.EITHER_VENDOR_NAME_OR_ZIP_CODE
          },
        }
      },
      {
        name: 'address',
        type: 'text',
        label: 'Address',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
      },
      {
        name: 'city',
        type: 'text',
        label: 'City',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3'
      },
      {
        name: 'state',
        type: 'select',
        label: 'State',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
        options: [],
      },

      {
        name: 'zip',
        type: 'text',
        label: 'ZIP',
        colSize: '12',
        colSizeSm: '3',
        colSizeMd: '3',
        colSizeLg: '3',
        validation: {
          requiredEitherOr: {
            fields: ['state', 'zip'],
            message: VALIDATION_MESSAGES.STATE_OR_ZIP_REQUIRED
          },
          pattern: { value: US_ZIP_CODE_REGEX, message: VALIDATION_MESSAGES.ZIP_CODE_INVALID },
        },
        limitations: {
          pattern: ZIP_INPUT_REGEX,
          maxLength: 5
        }
      },
      {
        name: 'vendorType',
        type: 'select',
        label: 'Vendor Type',
        colSize: '12',
        colSizeSm: '4',
        colSizeMd: '4',
        colSizeLg: '4',
        options: [],
      },
      {
        name: 'vendorSpecialty',
        type: 'multiselect',
        label: 'Vendor Specialty',
        colSize: '12',
        colSizeSm: '4',
        colSizeMd: '4',
        colSizeLg: '4',
        options: [],
        groupBy: 'group',
        // filter: true,
      },
      {
        name: 'approvalStatus',
        type: 'multiselect',
        label: 'Approval Status',
        colSize: '12',
        colSizeSm: '4',
        colSizeMd: '4',
        colSizeLg: '4',
        options: [],
      },
      {
        name: 'searchDistance',
        type: 'select',
        label: 'Search Distance (Miles)',
        colSize: '12',
        colSizeSm: '12',
        colSizeMd: '4',
        colSizeLg: '3',
        options: SEARCH_MILES_OPTIONS,
      }
    ],
    rows: [
      ['vendorName'],
      ['address', 'city', 'state', 'zip'],
      ['vendorType', 'vendorSpecialty', 'approvalStatus'],
      ['searchDistance']
    ],
    initialValues: {
      vendorName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      vendorType: '',
      vendorSpecialty: '',
      approvalStatus: '',
      searchDistance: '25'
    }
  },
  external: {
    local: {
      title: "Emergency Property Services Search",
      helpText: 'To Search, enter either your City and State or ZIP. All other fields are optional and can help refine results. ',
      fields: [
        {
          name: "searchType",
          type: "select",
          label: "Property Search Type",
          options: PROPERTY_SEARCH_TYPE_OPTIONS,
          colSize: '12',
          colSizeSm: '12',
          colSizeMd: '3',
          colSizeLg: '3',
        },
        {
          name: 'address',
          type: 'text',
          label: 'Address',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
        },
        {
          name: 'city',
          type: 'text',
          label: 'City',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          validation: {
            conditionalGroup: {
              fields: [['city', 'state'], 'zip'],
              message: VALIDATION_MESSAGES.CITY_STATE_OR_ZIP_REQUIRED
            }
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          options: [],
          validation: {
            conditionalGroup: {
              fields: [['city', 'state'], 'zip'],
              message: VALIDATION_MESSAGES.CITY_STATE_OR_ZIP_REQUIRED
            }
          }
        },
        {
          name: 'zip',
          type: 'text',
          label: 'ZIP',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          validation: {
            conditionalGroup: {
              fields: [['city', 'state'], 'zip'],
              message: VALIDATION_MESSAGES.CITY_STATE_OR_ZIP_REQUIRED
            },
            pattern: { value: US_ZIP_CODE_REGEX, message: VALIDATION_MESSAGES.ZIP_CODE_INVALID },
          },
          limitations: {
            maxLength: 5,
            pattern: ZIP_INPUT_REGEX
          }
        },
        {
          name: 'vendorSpecialty',
          type: 'multiselect',
          label: 'Vendor Specialty',
          colSize: '12',
          colSizeSm: '4',
          colSizeMd: '4',
          colSizeLg: '4',
          options: [],
          validation: {
            required: { value: true, message: VALIDATION_MESSAGES.VENDOR_SPECIALTY_REQUIRED }
          },
        },
        {
          name: 'searchDistance',
          type: 'select',
          label: 'Search Distance (Miles)',
          colSize: '12',
          colSizeSm: '12',
          colSizeMd: '4',
          colSizeLg: '3',
          options: SEARCH_MILES_OPTIONS,
        }
      ],
      rows: [
        ["address", "city", "state", "zip"],
        ["vendorSpecialty", "searchDistance"]
      ],
      initialValues: {
        searchType: 'local',
        address: '',
        city: '',
        state: '',
        zip: '',
        vendorSpecialty: '',
        searchDistance: '25'
      }
    },
    name: {
      title: "Emergency Property Services Search",
      helpText: 'Enter Vendor Name and State to search. City is optional and can help refine results.',
      fields: [
        {
          name: "searchType",
          type: "select",
          label: "Property Search Type",
          options: PROPERTY_SEARCH_TYPE_OPTIONS,
          colSize: '3',
          colSizeSm: '12',
          colSizeMd: '3',
          colSizeLg: '3',
        },
        {
          name: 'vendorName',
          type: 'text',
          label: 'Vendor Name',
          colSize: '12',
          colSizeSm: '7',
          colSizeMd: '7',
          colSizeLg: '7',
          validation: {
            minLength: { value: 3, message: VALIDATION_MESSAGES.VENDOR_NAME_MIN_LENGTH },
            required: { value: true, message: VALIDATION_MESSAGES.VENDOR_NAME_REQUIRED }
          }
        },
        {
          name: 'city',
          type: 'text',
          label: 'City',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',

        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          colSize: '12',
          colSizeSm: '3',
          colSizeMd: '3',
          colSizeLg: '3',
          options: [],
          validation: {
            required: { value: true, message: VALIDATION_MESSAGES.STATE_REQUIRED }
          }
        },
      ],
      rows: [
        ["vendorName"],
        ["city", "state"]
      ],
      initialValues: {
        searchType: 'name',
        vendorName: '',
        city: '',
        state: '',
        approvalStatus: '',
      }
    },
  }
};

/**
 * Load search configurations with dynamic state options from API
 * This function fetches state options and populates all state fields in the search configs
 * 
 * @param configType - Type of search config to load ('auto' | 'property' | 'propertyClaim' | 'contract')
 * @returns Promise - Configuration object with populated state options
 */
export async function loadSearchConfigs(configType: 'auto' | 'property' | 'propertyClaim' | 'contract' = 'auto') {
  // Fetch state options from API
  const stateOptions = await getStateOptions();

  // Fetch vendor type and grouped specialty options based on config type
  let vendorTypeOptions: Array<{ id: string; value: string }> = [];
  let vendorSpecialtyGrouped: Array<{ id: string; value: string; group: string }> = [];
  if (configType === 'property') {
    vendorTypeOptions = await getPropertyVendorTypeOptions();
    vendorSpecialtyGrouped = await buildPropertyServiceCodeOptions();
  }

  // Helper function to update state fields in a form config
  const updateStateFieldsInConfig = (formConfig: FormConfig) => {
    return {
      ...formConfig,
      fields: formConfig.fields.map(field => {
        if (field.name === 'state') {
          return { ...field, options: stateOptions };
        }
        return field;
      })
    };
  };

  // Helper function to update vendor fields (vendorType, vendorSpecialty) in a form config
  const updateVendorFieldsInConfig = (formConfig: FormConfig) => {
    // If not property or contract, return unchanged
    if (vendorTypeOptions.length === 0 && vendorSpecialtyGrouped.length === 0) return formConfig;

    const withVendor = {
      ...formConfig,
      fields: formConfig.fields.map(field => {
        if (field.name === 'vendorType') {
          // Prepend empty option for optional selection
          const opts = vendorTypeOptions[0]?.id === '' ? vendorTypeOptions : [{ id: '', value: '' }, ...vendorTypeOptions];
          return { ...field, options: opts };
        }
        if (field.name === 'vendorSpecialty') {
          return { ...field, options: vendorSpecialtyGrouped, groupBy: 'group' };
        }
        return field;
      })
    };
    return withVendor;
  };

  // Helper function to update state fields in all configs (admin + internal)
  const updateAllStateFields = (configs: CombinedFormConfigs) => {
    const updated = structuredClone(configs);
    updated.admin = updateStateFieldsInConfig(updated.admin);
    updated.admin = updateVendorFieldsInConfig(updated.admin);

    if (updated.external) {
      Object.keys(updated.external).forEach(key => {
        const externalConfig = updated.external[key];
        if (externalConfig) {
          updated.external[key] = updateStateFieldsInConfig(externalConfig);
          updated.external[key] = updateVendorFieldsInConfig(updated.external[key]);
        }
      });
    }

    return updated;
  };

  // Load the appropriate config type
  if (configType === 'auto') {
    return updateAllStateFields(AUTO_SEARCH_CONFIGS);
  } else if (configType === 'property') {
    return updateAllStateFields(PROPERTY_SEARCH_CONFIGS);
  }

  // Default to auto config
  return updateAllStateFields(AUTO_SEARCH_CONFIGS);
}

/**
 * Load Auto Search configurations with dynamic state options
 */
export async function loadAutoSearchConfigs(): Promise<CombinedFormConfigs> {
  return loadSearchConfigs('auto');
}

/**
 * Load Property Search configurations with dynamic state options
 */
export async function loadPropertySearchConfigs(): Promise<CombinedFormConfigs> {
  return loadSearchConfigs('property');
}


